import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/coralreef";
import type { BootFailure, BootStatus } from "@/shell-handle/bootController";

const COPY: Record<BootFailure, { title: string; hint: string }> = {
  unreachable: {
    title: "Could not connect to the server",
    hint: "Check your network connection and try again.",
  },
  error: {
    title: "The app could not start",
    hint: "Try again. If it keeps happening, contact support.",
  },
};

export function ShellBootError({ boot }: { boot: BootStatus }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(boot.failed.get());
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    setFailed(boot.failed.get());
    return boot.failed.subscribe(setFailed);
  }, [boot]);

  if (!failed) return null;

  const retry = () => {
    setRetrying(true);
    void boot.retry().finally(() => setRetrying(false));
  };

  return (
    <div
      role="alert"
      data-testid="shell-boot-error"
      className="fixed inset-0 z-[1000] flex flex-col items-center justify-center gap-4 bg-observe-darkest p-6 text-center text-observe-textLight"
    >
      <h2 className="text-xl font-semibold">{t(COPY[failed].title)}</h2>
      <p>{t(COPY[failed].hint)}</p>
      <Button
        data-testid="shell-boot-retry"
        content={t("Retry")}
        appearance="hollow"
        loading={retrying}
        onClick={retry}
      />
    </div>
  );
}
